import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	inject,
	input,
	signal,
	ViewChild,
} from '@angular/core';
import {
	DocumentPreviewComponent,
	DocumentPreviewProperties,
} from 'src/app/shared/components/document-preview/document-preview.component';
import { FormFileMetadata } from '@models/client-intake';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { NgClass } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
	selector: 'app-intake-responses-row',
	templateUrl: './intake-responses-row.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [ButtonComponent, NgClass, MatTooltipModule],
	styles: [
		`
			:host {
				display: contents;
			}
		`,
	],
})
export class IntakeResponsesRowComponent {
	@ViewChild('fileNameElement', { static: false })
	fileNameElement!: ElementRef<HTMLElement>;
	@ViewChild('textElement', { static: false })
	textElement!: ElementRef<HTMLElement>;

	private drawerService = inject(DrawerService);

	showViewDetails_ = signal(false);

	answer = input<string | FormFileMetadata | null>();
	friendlyAnswer = input<string | null>();
	clientName = input<string>('');
	isTitleCell = input<boolean>(false);

	isFile_ = computed(() => {
		return (
			this.answer() !== null &&
			typeof this.answer() === 'object' &&
			// eslint-disable-next-line no-prototype-builtins
			this.answer()?.hasOwnProperty('fileName')
		);
	});

	answerText_ = computed(() => {
		if (this.friendlyAnswer()) {
			return this.friendlyAnswer();
		}

		if (!this.isFile_()) {
			return this.answer() as string;
		}
	});

	answerFile_ = computed(() => {
		if (this.isFile_()) {
			return this.answer() as FormFileMetadata;
		}
	});

	fileNameTooltip_ = computed(() => {
		if (!this.isFile_() || !this.answerFile_()?.fileName) {
			return '';
		}

		return this.answerFile_().fileName;
	});

	textElementTooltip_ = computed(() => {
		if (!this.answerText_() || this.answerText_() === '-') {
			return '';
		}

		return this.answerText_();
	});

	answerIcon_ = computed(() => {
		const file = this.answerFile_();

		switch (file?.type) {
			case 'application/pdf':
				return 'pdf';
			case 'image/jpeg':
			case 'image/png':
			case 'image/gif':
			case 'image/webp':
			case 'image/svg+xml':
			case 'image/bmp':
			case 'image/tiff':
				return 'image';
			case 'word':
			case 'application/msword':
			case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
				return 'word';
			case 'excel':
			case 'text/csv':
			case 'application/vnd.ms-excel':
			case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
				return 'excel';
			default:
				return 'form';
		}
	});

	setShowViewDetails(value: boolean): void {
		if (!this.isFile_()) {
			return;
		}

		this.showViewDetails_.set(value);
	}

	openFile(): void {
		const properties: DocumentPreviewProperties[] = [
			{
				property: 'Client',
				value: this.clientName() || '',
			},
			{
				property: 'Document',
				value: this.answerFile_()?.fileName || '',
			},
		];

		this.drawerService.openDrawer(DocumentPreviewComponent, {
			title: 'View Document',
			noStyle: true,
			data: {
				url: this.answerFile_()?.firebaseStorageUrl,
				properties,
			},
		});
	}
}
